
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Clock, User, MessageSquare, Phone } from 'lucide-react';

const EmergencyConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState<number>(0);
  const [secondsElapsed, setSecondsElapsed] = useState<number>(0);
  
  // Simulate volunteers viewing and accepting the request
  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsElapsed(prev => prev + 1);
      
      // Randomly increase volunteer count
      if (Math.random() > 0.7 && volunteers < 5) {
        setVolunteers(prev => prev + 1);
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [volunteers]);
  
  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  return (
    <Layout>
      <Helmet>
        <title>Emergency Response | CareConnect</title>
        <meta name="description" content="Track the status of your emergency volunteer request in real-time." />
      </Helmet>
      
      <section className="pt-32 pb-20 bg-gradient-to-br from-care-50 to-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <Clock className="h-8 w-8 text-red-600" />
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight">
                Emergency Request Sent
              </h1>
              
              <p className="text-lg text-muted-foreground mb-2">
                Your request has been sent to volunteers in the area.
              </p>
              
              <div className="inline-block bg-amber-50 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                Request active for {formatTime(secondsElapsed)}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass-morphism rounded-xl p-6 mb-8"
            >
              <div className="border-b border-gray-200 pb-4 mb-4">
                <h2 className="text-xl font-semibold text-care-800">Request Status</h2>
                <p className="text-sm text-muted-foreground">Request ID: EMRG-{Math.floor(10000 + Math.random() * 90000)}</p>
              </div>
              
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-care-600 mr-3" />
                    <div>
                      <p className="font-medium text-care-800">Volunteers Notified</p>
                      <p className="text-sm text-muted-foreground">Within 5 mile radius</p>
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-care-600">8</div>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-green-600 mr-3" />
                    <div>
                      <p className="font-medium text-green-800">Volunteers Viewed</p>
                      <p className="text-sm text-muted-foreground">Seen request details</p>
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-green-600">{volunteers}</div>
                </div>
                
                {volunteers > 0 && (
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                    <p className="text-sm text-green-800">
                      <span className="font-medium">Good news!</span> {volunteers} volunteer{volunteers > 1 ? 's have' : ' has'} seen your request and {volunteers > 1 ? 'are' : 'is'} considering responding.
                    </p>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="font-medium text-care-800 mb-2">Next Steps</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    You will receive an immediate notification when a volunteer accepts your request. They will then proceed to the specified address.
                  </p>
                  
                  <div className="space-y-3">
                    <Button 
                      variant="outline" 
                      className="w-full flex items-center justify-center gap-2 border-care-200 text-care-700"
                    >
                      <Phone className="h-4 w-4" />
                      Call Emergency Services
                    </Button>
                    
                    <Button 
                      className="w-full flex items-center justify-center gap-2 bg-care-500 hover:bg-care-600 text-white"
                    >
                      <MessageSquare className="h-4 w-4" />
                      Send Additional Info
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="outline" 
                onClick={() => navigate('/emergency')}
                className="border-care-200 text-care-700"
              >
                Return to Emergency Page
              </Button>
              <Button onClick={() => navigate('/')}>
                Go to Home
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EmergencyConfirmation;
